import { useState, useEffect, useCallback } from "react";
import { Box, useTheme } from "@mui/material";
import ChannelSidebar from "../../components/channel/channel-sidebar";
import MessageArea from "../../components/channel/message-area";
import ActiveUsersSidebar, {
  EnrichedChannelMember,
} from "../../components/channel/active-users-sidebar";
import CustomAppBar from "../../components/shared/CustomAppBar";
import { ChannelService } from "../../services/channelService";
import { ChannelMessageDto } from "../../types/channelMessage";
import { GlobalUser, ProfileDto } from "../../types/UserToken";
import { useAuth } from "../../context/AuthContext";
import { ChannelDto } from "../../types/channel";
import {
  getActiveUsers,
  getUsersConnections,
  joinChanel,
  joinGlobalChannel,
  onJoinedChannel,
  onJoinedGlobalChannel,
} from "../../services/signalRService";
import { useSignalRConnection } from "../../hooks/connectionSignalR";
import { useMediaQuery } from "@mui/system";

export default function ChatLayout() {
  const [selectedTab, setSelectedTab] = useState<"channels" | "global">(
    "channels"
  );
  const [selectedChannel, setSelectedChannel] = useState<ChannelDto | null>(
    null
  );
  const [channels, setChannels] = useState<ChannelDto[]>([]);
  const [messages, setMessages] = useState<ChannelMessageDto[]>([]);
  const [activeMembers, setActiveMembers] = useState<EnrichedChannelMember[]>(
    []
  );
  const [showChats, setShowChats] = useState(false);
  const [publicChannels, setPublicChannels] = useState<ChannelDto[]>([]);
  const [activeUsers, setActiveUsers] = useState<number[]>([])
  const [globalActiveUsers, setGlobalActiveUsers] = useState<GlobalUser[]>([])

  const { userToken } = useAuth();
  const currentUser: ProfileDto = {
    id: userToken?.user?.profiles[0]?.id ?? 0,
    username: userToken?.user?.profiles[0]?.username ?? "",
    avatarUrl: userToken?.user?.photo ?? "/placeholder.svg",
    roleName: userToken?.user?.profiles[0]?.roleName ?? "member",
    userProfileId: userToken?.user?.profiles[0]?.id ?? 0,
  };

  const connected = useSignalRConnection(currentUser?.id.toString());

  console.log(channels);

  useEffect(() => {
    const fetchUserConnections = async () => {
      if (!connected) {
        console.error("No conectado a SignalR");
        return;
      }
      try {
        if (selectedChannel || selectedTab === "global") {
          await getUsersConnections();
        }
      } catch (error) {
        console.error("Error al obtener conexiones de usuario:", error);
      }
    };
    fetchUserConnections();
  }, [connected, selectedChannel, selectedTab]);

  useEffect(() => {
    const fetchActiveUsers = async () => {
      if (selectedChannel || selectedTab === "global") {
        const users = await getActiveUsers();
        setActiveUsers(users);
      }
    };
    fetchActiveUsers();
  }, [selectedChannel, selectedTab]);

  useEffect(() => {
    const handleJoinedChannel = (channelId: number) => {
      if (!selectedChannel || selectedTab !== "channels") return;
      console.log("Unido al canal:", channelId);
    };

    const handleJoinedGlobalChannel = (channelId: number) => {
      if (selectedTab !== "global") return;
      console.log("Unido al canal global:", channelId);
    };

    onJoinedChannel(handleJoinedChannel);
    onJoinedGlobalChannel(handleJoinedGlobalChannel);
  }, [selectedChannel, selectedTab]);

  const JoinChannel = async (channelId: number, profileId: number) => {
    if (!connected) {
      console.error("No conectado a SignalR");
      return;
    }

    try {
      await joinChanel(channelId, profileId);
      console.log("Unido al canal:", channelId);
    } catch (error) {
      console.error("Error al unirse al canal:", error);
    }
  };

  const JoinGlobalChannel = useCallback(
    async (profileId: number) => {
      if (!connected) {
        console.error("No conectado a SignalR");
        return;
      }

      try {
        await joinGlobalChannel("global", profileId);
        console.log("Unido al canal global:", profileId);
      } catch (error) {
        console.error("Error al unirse al canal:", error);
      }
    },
    [connected]
  );

  const handleChannelSelect = (channel: ChannelDto) => {
    setSelectedChannel(channel);
    setSelectedTab("channels");
    JoinChannel(channel.id, currentUser.id);
    setShowChats(false);
  };

  useEffect(() => {
    const joinGlobalChannel = async () => {
      if (selectedTab === "global") {
        await JoinGlobalChannel(currentUser.id);
      }
    };

    joinGlobalChannel();
  }, [selectedTab, currentUser.id, JoinGlobalChannel]);

  const fetchChannels = useCallback(async () => {
    try {
      const result = await ChannelService.getChannelsForProfile(currentUser.id);
      setChannels(result);
    } catch (error) {
      console.error("Error fetching channels:", error);
    }
  }, [currentUser.id, setChannels]);

  const fetchChannelData = useCallback(async () => {
  if (selectedTab === "global") {
    setMessages([]);
    setActiveMembers([]);
    return;
  }

  if (!selectedChannel) return;

  try {
    const members = await ChannelService.getChannelMembers(selectedChannel.id);

    const enrichedMembers = members.map((member: any) => ({
      ...member,
      profile: {
        id: member.profile?.profileId || member.profileId,
        username: member.profile?.fullName || "Usuario desconocido",
        avatarUrl: member.profile?.profileImage || "/placeholder.svg",
        roleName: member.profile?.roleName || "Miembro",
        userId: member.profile?.userId ?? 0,
      },
    }));

    const msgs = await ChannelService.getChannelMessages(selectedChannel.id);

    setActiveMembers(enrichedMembers);
    setMessages(msgs);
  } catch (error) {
    console.error("Error al cargar datos del canal:", error);
    setActiveMembers([]);
    setMessages([]);
  }
}, [selectedTab, selectedChannel, setActiveMembers, setMessages]);

  useEffect(() => {
    fetchChannelData();
  }, [fetchChannelData]);


  const handleCreateChannel = (newChannel: ChannelDto) => {
    setChannels((prev) => [...prev, newChannel]);
    setSelectedChannel(newChannel);
  };

  const handleShowChats = () => {
    setShowChats((prev) => !prev);
  };

  const fetchPublicChannels = useCallback(async () => {
    try {
      const result = await ChannelService.getPublicChannels();
      setPublicChannels(result);
    } catch (error) {
      console.error("Error fetching public channels:", error);
    }
  }, [setPublicChannels]);

  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  
  
    useEffect(() => {
      const fetchGlobalActiveUsers = async () => {
        try {
          const response = await ChannelService.getActiveUserProfiles()
          const activeUserIds = response.data.filter(
            (user: GlobalUser) => activeUsers.includes(user.id)
          )
  
          setGlobalActiveUsers(activeUserIds)
        } catch (error) {
          console.error("Error fetching global active users:", error)
        }
      }
  
      if (selectedTab === "global" && activeUsers.length > 0) {
        fetchGlobalActiveUsers()
      }
    }, [selectedTab, activeUsers])

    const sortedMembers = [...activeMembers].sort((a, b) => {
      const profileA = a.profile
      const profileB = b.profile
  
      if (activeUsers.includes(profileA?.id) && !activeUsers.includes(profileB?.id)) return -1
      if (!activeUsers.includes(profileA?.id) && activeUsers.includes(profileB?.id)) return 1

      if (a.role === "Admin" && b.role !== "Admin") return -1
      if (a.role !== "Admin" && b.role === "Admin") return 1
  
      return (profileA?.username || "").localeCompare(profileB?.username || "")
    })
  
    const onlineMembers = sortedMembers.filter((m) =>
      activeUsers.includes(m.profile.id)
    )
    const offlineMembers = sortedMembers.filter(
      (m) => !activeUsers.includes(m.profile.id)
    )

  return (
    <>
      {isMdUp && <CustomAppBar />}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: { xs: "calc(100vh - 10vh)", md: "89vh" },
          width: "100vw",
          mt: isMdUp ? "10vh" : 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flex: 1,
            width: "100%",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              overflow: "auto",
              borderRight: "1px solid",
              borderColor: "divider",
              display: {
                xs:
                  (selectedChannel && !showChats) ||
                  (selectedTab === "global" && !showChats)
                    ? "none"
                    : "flex",
                md: "flex",
              },
              width: { xs: "100%", md: "20%" },
              height: "100%",
            }}
          >
            <ChannelSidebar
              selectedTab={selectedTab}
              onTabChange={setSelectedTab}
              selectedChannel={selectedChannel}
              onChannelSelect={handleChannelSelect}
              onCreateChannel={handleCreateChannel}
              currentUser={currentUser}
              globalMessages={[]}
              handleShowChats={handleShowChats}
              publicChannels={publicChannels}
              channels={channels}
              fetchChannels={fetchChannels}
              fetchPublicChannels={fetchPublicChannels}
              setSelectedChannel={setSelectedChannel}
              fetchChannelData={fetchChannelData}
            />
          </Box>
          <Box
            sx={{
              flexGrow: 1,
              overflow: "hidden",
              width: { xs: "100%", md: "50%" },
              height: "100%",
              display: {
                xs:
                  (selectedChannel && !showChats) ||
                  (selectedTab === "global" && !showChats)
                    ? "flex"
                    : "none",
                md: "flex",
              },
            }}
          >
            <MessageArea
              messages={messages}
              channel={selectedChannel}
              members={activeMembers}
              currentUser={currentUser}
              isGlobal={selectedTab === "global"}
              handleShowChats={handleShowChats}
              fetchChannels={fetchChannels}
              fetchPublicChannels={fetchPublicChannels}
              setSelectedTab={setSelectedTab}
              setSelectedChannel={setSelectedChannel}
              activeUserIds={activeUsers}
            />
          </Box>
          <Box
            sx={{
              width: { xs: "100%", md: "20%" },
              height: "100%",
              overflow: "auto",
              borderLeft: "1px solid",
              borderColor: "divider",
              display: { xs: "none", md: "flex" },
            }}
          >
            <ActiveUsersSidebar
              members={activeMembers}
              currentUser={currentUser}
              isGlobal={selectedTab === "global"}
              globalActiveUsers={globalActiveUsers}
              onlineMembers={onlineMembers}
              offlineMembers={offlineMembers}
            />
          </Box>
        </Box>
      </Box>
    </>
  );
}
